import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useAdmin } from './AdminContext';

export default function AuditTab({ refreshTrigger }) {
  const { adminFetch, API_URL } = useAdmin();
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditActions, setAuditActions] = useState([]);
  const [auditFilterCategory, setAuditFilterCategory] = useState('all');
  const [auditFilterAction, setAuditFilterAction] = useState('all');
  const [auditPage, setAuditPage] = useState(0);

  const loadAuditLog = useCallback(async (page = 0, category = 'all', action = 'all') => {
    try {
      let url = `${API_URL}/api/v2/admin/audit-log?skip=${page * 50}&limit=50`;
      if (category !== 'all') url += `&category=${category}`;
      if (action !== 'all') url += `&action=${action}`;
      const response = await adminFetch(url);
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
        setAuditTotal(data.total || 0);
        setAuditActions(data.actions || []);
      }
    } catch (error) {
      console.error('Error loading audit log:', error);
    }
  }, [adminFetch, API_URL]);

  useEffect(() => { loadAuditLog(); }, [loadAuditLog, refreshTrigger]);

  return (
    <div className="space-y-6" data-testid="audit-log-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Audit Log</h2>
        <Button onClick={() => loadAuditLog(auditPage, auditFilterCategory, auditFilterAction)}
          variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="audit-refresh-btn">
          Refresh
        </Button>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Category</label>
              <select data-testid="audit-filter-category" value={auditFilterCategory}
                onChange={(e) => { setAuditFilterCategory(e.target.value); setAuditPage(0); loadAuditLog(0, e.target.value, auditFilterAction); }}
                className="bg-slate-700 text-white border border-slate-600 rounded px-3 py-1.5 text-sm">
                <option value="all">All Categories</option>
                <option value="admin">Admin</option>
                <option value="auth">Auth</option>
                <option value="security">Security</option>
              </select>
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Action</label>
              <select data-testid="audit-filter-action" value={auditFilterAction}
                onChange={(e) => { setAuditFilterAction(e.target.value); setAuditPage(0); loadAuditLog(0, auditFilterCategory, e.target.value); }}
                className="bg-slate-700 text-white border border-slate-600 rounded px-3 py-1.5 text-sm">
                <option value="all">All Actions</option>
                {auditActions.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <span className="text-slate-500 text-sm">{auditTotal} entries</span>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead>
                <tr className="border-b border-slate-700 text-left">
                  <th className="p-3 text-slate-400 font-medium">Timestamp</th>
                  <th className="p-3 text-slate-400 font-medium">Category</th>
                  <th className="p-3 text-slate-400 font-medium">Action</th>
                  <th className="p-3 text-slate-400 font-medium">Actor</th>
                  <th className="p-3 text-slate-400 font-medium">IP</th>
                  <th className="p-3 text-slate-400 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-500">No audit log entries found</td></tr>
                ) : (
                  auditLogs.map((log, i) => (
                    <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`audit-row-${i}`}>
                      <td className="p-3 text-slate-300 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          log.category === 'security' ? 'bg-red-500/20 text-red-400' :
                          log.category === 'admin' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>{log.category}</span>
                      </td>
                      <td className="p-3 text-white font-mono text-xs">{log.action}</td>
                      <td className="p-3 text-slate-300">{log.actor}</td>
                      <td className="p-3 text-slate-500 font-mono text-xs">{log.ip || '-'}</td>
                      <td className="p-3 text-slate-400 text-xs max-w-xs truncate">
                        {Object.keys(log.details || {}).length > 0
                          ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                          : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {auditTotal > 50 && (
            <div className="flex items-center justify-between p-3 border-t border-slate-700">
              <Button onClick={() => { const p = Math.max(0, auditPage - 1); setAuditPage(p); loadAuditLog(p, auditFilterCategory, auditFilterAction); }}
                disabled={auditPage === 0} variant="outline" size="sm" className="border-slate-600 text-slate-300" data-testid="audit-prev-btn">
                Previous
              </Button>
              <span className="text-slate-400 text-sm">Page {auditPage + 1} of {Math.ceil(auditTotal / 50)}</span>
              <Button onClick={() => { const p = auditPage + 1; setAuditPage(p); loadAuditLog(p, auditFilterCategory, auditFilterAction); }}
                disabled={(auditPage + 1) * 50 >= auditTotal} variant="outline" size="sm" className="border-slate-600 text-slate-300" data-testid="audit-next-btn">
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
